import { useState, useMemo } from "react";
import {
  trips as mockTrips,
  tripStats as mockTripStats,
  tripVisitors as mockTripVisitors,
  visitorQAHistory as mockVisitorQAHistory,
} from "@/mocks/trips";
import type { Trip, TripVisitor } from "@/mocks/trips";
import { useApi } from "@/hooks/useApi";

interface VisitorDrawerProps {
  visitor: TripVisitor | null;
  onClose: () => void;
}

function VisitorDrawer({ visitor, onClose }: VisitorDrawerProps) {
  const { data: visitorQAHistory } = useApi<typeof mockVisitorQAHistory>(
    "/data/visitorQAHistory",
    mockVisitorQAHistory,
  );

  if (!visitor) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">游客详情</h2>
            <p className="text-sm text-gray-500 mt-0.5">{visitor.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-xs text-gray-500 block mb-1">姓名</span><span className="text-sm font-medium text-gray-900">{visitor.name}</span></div>
              <div><span className="text-xs text-gray-500 block mb-1">绑定设备</span><span className="text-sm font-medium text-gray-900 font-mono">{visitor.deviceNo}</span></div>
              <div><span className="text-xs text-gray-500 block mb-1">问答数</span><span className="text-sm font-medium text-gray-900">{visitor.questionCount}</span></div>
              <div><span className="text-xs text-gray-500 block mb-1">换脸数</span><span className="text-sm font-medium text-gray-900">{visitor.faceswapCount}</span></div>
              <div><span className="text-xs text-gray-500 block mb-1">报告生成</span><span className={`text-sm font-medium ${visitor.reportGenerated ? "text-emerald-600" : "text-gray-500"}`}>{visitor.reportGenerated ? "已生成" : "未生成"}</span></div>
              <div><span className="text-xs text-gray-500 block mb-1">报告下载</span><span className="text-sm font-medium text-gray-900">{visitor.reportDownloads} 次</span></div>
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">问答时间线</h3>
          <div className="space-y-3">
            {visitorQAHistory.map((qa, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">{qa.time}</span>
                </div>
                <p className="text-sm font-medium text-gray-800 mb-1">Q: {qa.question}</p>
                <p className="text-sm text-gray-600">A: {qa.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TripsPage() {
  const { data: trips } = useApi<typeof mockTrips>("/data/trips", mockTrips);
  const { data: tripStats } = useApi<typeof mockTripStats>(
    "/data/tripStats",
    mockTripStats,
  );
  const { data: tripVisitors } = useApi<typeof mockTripVisitors>(
    "/data/tripVisitors",
    mockTripVisitors,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<TripVisitor | null>(null);

  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const matchesSearch = searchQuery === "" || trip.ship.includes(searchQuery);
      const matchesStatus = statusFilter === "all" || trip.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [trips, searchQuery, statusFilter]);

  const statusConfig = {
    ongoing: { label: "进行中", color: "bg-teal-100 text-teal-700" },
    completed: { label: "已完成", color: "bg-gray-100 text-gray-700" },
    pending: { label: "待开始", color: "bg-amber-100 text-amber-700" },
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">游客与行程</h1>
          <p className="text-sm text-gray-500 mt-1">以船次/行程维度查看游客使用数据</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: "今日船次", value: tripStats.todayTrips, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "总游客数", value: tripStats.totalVisitors, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
          { label: "总问答数", value: tripStats.totalQuestions, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "总换脸数", value: tripStats.totalFaceswaps, color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索船次名称" className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
            <option value="all">全部状态</option>
            <option value="ongoing">进行中</option>
            <option value="completed">已完成</option>
            <option value="pending">待开始</option>
          </select>
          {(searchQuery || statusFilter !== "all") && (
            <button onClick={() => { setSearchQuery(""); setStatusFilter("all"); }} className="text-sm text-gray-500 hover:text-gray-700">重置</button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">船次</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日期</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">发放眼镜</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">活跃游客</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">问答总数</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">换脸数</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">报告生成</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTrips.map((trip) => (
                <tr key={trip.id} className="hover:bg-teal-50/30 transition-colors border-l-[3px] border-transparent hover:border-teal-500">
                  <td className="px-4 py-3">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{trip.ship}</span>
                      <span className="text-xs text-gray-400 block">{trip.batch}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.date}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.glassesIssued}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.activeVisitors}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.questions}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.faceswapCount}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">{trip.reportsGenerated}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[trip.status].color}`}>
                      {statusConfig[trip.status].label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelectedTrip(selectedTrip?.id === trip.id ? null : trip)} className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors">
                      {selectedTrip?.id === trip.id ? "收起" : "查看详情"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedTrip && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{selectedTrip.ship} - {selectedTrip.batch}</h3>
              <p className="text-sm text-gray-500">{selectedTrip.date} · {selectedTrip.glassesIssued} 台眼镜 · {selectedTrip.activeVisitors} 位游客</p>
            </div>
            <button onClick={() => setSelectedTrip(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">游客</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">设备</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">问答数</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">换脸数</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">报告</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tripVisitors.map((visitor) => (
                  <tr key={visitor.id} className="hover:bg-teal-50/30 transition-colors cursor-pointer border-l-[3px] border-transparent hover:border-teal-500" onClick={() => setSelectedVisitor(visitor)}>
                    <td className="px-4 py-3"><span className="text-sm font-medium text-gray-900">{visitor.name}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-600 font-mono">{visitor.deviceNo}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-700">{visitor.questionCount}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-700">{visitor.faceswapCount}</span></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${visitor.reportGenerated ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                        {visitor.reportGenerated ? "已生成" : "未生成"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${visitor.status === "active" ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-700"}`}>
                        {visitor.status === "active" ? "活跃" : "离线"}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button className="text-xs text-teal-600 hover:text-teal-700 font-medium">查看详情</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedVisitor && <VisitorDrawer visitor={selectedVisitor} onClose={() => setSelectedVisitor(null)} />}
    </div>
  );
}

export default TripsPage;